import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { 
  Video, Plus, Edit, Trash2, Save, X, 
  Folder, FolderPlus, Eye, ChevronDown, ChevronUp,
  GripVertical, Link as LinkIcon, Play
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function VideoCoursesManagement() {
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState([]);
  
  // Form states
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  
  const [categoryForm, setCategoryForm] = useState({ title: '', description: '', order: 0 });
  const [videoForm, setVideoForm] = useState({ category_id: '', title: '', vimeo_url: '', order: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/video-courses/all`, { withCredentials: true });
      setCategories(res.data.categories);
      setVideos(res.data.videos);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Category CRUD
  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/video-courses/categories/${editingCategory.id}`, categoryForm, { withCredentials: true });
        toast.success('Kategori güncellendi');
      } else {
        await axios.post(`${API}/admin/video-courses/categories`, categoryForm, { withCredentials: true });
        toast.success('Kategori oluşturuldu');
      }
      setShowCategoryForm(false);
      setEditingCategory(null);
      setCategoryForm({ title: '', description: '', order: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Bu kategoriyi ve içindeki tüm videoları silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/admin/video-courses/categories/${categoryId}`, { withCredentials: true });
      toast.success('Kategori silindi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    }
  };

  const startEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      title: category.title,
      description: category.description || '',
      order: category.order || 0
    });
    setShowCategoryForm(true);
  };

  // Video CRUD
  const handleSaveVideo = async () => {
    if (!videoForm.category_id) {
      toast.error('Lütfen bir kategori seçin');
      return;
    }
    if (!videoForm.vimeo_url) {
      toast.error('Lütfen Vimeo linki girin');
      return;
    }
    
    try {
      if (editingVideo) {
        await axios.put(`${API}/admin/video-courses/videos/${editingVideo.id}`, videoForm, { withCredentials: true });
        toast.success('Video güncellendi');
      } else {
        await axios.post(`${API}/admin/video-courses/videos`, videoForm, { withCredentials: true });
        toast.success('Video eklendi');
      }
      setShowVideoForm(false);
      setEditingVideo(null);
      setVideoForm({ category_id: '', title: '', vimeo_url: '', order: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Bu videoyu silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/admin/video-courses/videos/${videoId}`, { withCredentials: true });
      toast.success('Video silindi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    }
  };

  const startEditVideo = (video) => {
    setEditingVideo(video);
    setVideoForm({
      category_id: video.category_id,
      title: video.title,
      vimeo_url: video.vimeo_url,
      order: video.order || 0
    });
    setShowVideoForm(true);
  };

  const startAddVideo = (categoryId = '') => {
    setEditingVideo(null);
    setVideoForm({ category_id: categoryId, title: '', vimeo_url: '', order: 0 });
    setShowVideoForm(true);
  };

  const getVideosForCategory = (categoryId) => {
    return videos.filter(v => v.category_id === categoryId).sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">📹 Videolu Dersler Yönetimi</h2>
          <p className="text-gray-500 mt-1">Vimeo videolarınızı kategorilere ayırarak yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => {
              setEditingCategory(null);
              setCategoryForm({ title: '', description: '', order: 0 });
              setShowCategoryForm(true);
            }}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <FolderPlus className="w-4 h-4 mr-2" />
            Yeni Kategori
          </Button>
          <Button 
            onClick={() => startAddVideo()}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Video
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-purple-500 rounded-xl">
              <Folder className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-700">{categories.length}</p>
              <p className="text-purple-600 text-sm">Kategori</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-500 rounded-xl">
              <Video className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{videos.length}</p>
              <p className="text-blue-600 text-sm">Toplam Video</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-500 rounded-xl">
              <Eye className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">
                {videos.reduce((sum, v) => sum + (v.watch_count || 0), 0)}
              </p>
              <p className="text-green-600 text-sm">Toplam İzlenme</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowCategoryForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Kategori Adı *</label>
                <Input
                  value={categoryForm.title}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Örn: İngilizce Eğitimi"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Açıklama</label>
                <Input
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Kategori açıklaması (opsiyonel)"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Sıra</label>
                <Input
                  type="number"
                  value={categoryForm.order}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowCategoryForm(false)}>
                  İptal
                </Button>
                <Button className="flex-1 bg-purple-600 hover:bg-purple-700" onClick={handleSaveCategory}>
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Video Form Modal */}
      {showVideoForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingVideo ? 'Video Düzenle' : 'Yeni Video Ekle'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowVideoForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Kategori *</label>
                <select
                  value={videoForm.category_id}
                  onChange={(e) => setVideoForm(prev => ({ ...prev, category_id: e.target.value }))}
                  className="w-full mt-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">Kategori Seçin</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Video Başlığı *</label>
                <Input
                  value={videoForm.title}
                  onChange={(e) => setVideoForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Örn: Ders 1 - Giriş"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Vimeo Linki *</label>
                <Input
                  value={videoForm.vimeo_url}
                  onChange={(e) => setVideoForm(prev => ({ ...prev, vimeo_url: e.target.value }))}
                  placeholder="https://vimeo.com/123456789"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Örnek: https://vimeo.com/123456789 veya https://player.vimeo.com/video/123456789
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Sıra</label>
                <Input
                  type="number"
                  value={videoForm.order}
                  onChange={(e) => setVideoForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowVideoForm(false)}>
                  İptal
                </Button>
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleSaveVideo}>
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Categories & Videos List */}
      <div className="space-y-4">
        {categories.length === 0 ? (
          <Card className="bg-gray-50">
            <CardContent className="p-8 text-center">
              <Folder className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-600">Henüz kategori yok</h3>
              <p className="text-gray-500 mt-1">Başlamak için bir kategori oluşturun</p>
            </CardContent>
          </Card>
        ) : (
          categories.map(category => {
            const categoryVideos = getVideosForCategory(category.id);
            const isExpanded = expandedCategories.includes(category.id);
            
            return (
              <Card key={category.id} className="overflow-hidden">
                {/* Category Header */}
                <div 
                  className="bg-gradient-to-r from-purple-500 to-indigo-600 p-4 cursor-pointer"
                  onClick={() => toggleCategory(category.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-lg">
                        <Folder className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">{category.title}</h3>
                        <p className="text-white/80 text-sm">{categoryVideos.length} video</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-white hover:bg-white/20"
                        onClick={(e) => { e.stopPropagation(); startEditCategory(category); }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-white hover:bg-red-500"
                        onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category.id); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-white" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-white" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Videos */}
                {isExpanded && (
                  <CardContent className="p-4 bg-gray-50">
                    <div className="space-y-2">
                      {categoryVideos.map((video, index) => (
                        <div 
                          key={video.id}
                          className="flex items-center gap-3 p-3 bg-white rounded-lg border hover:shadow-sm transition-all"
                        >
                          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 font-bold text-sm">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{video.title}</p>
                            <p className="text-xs text-gray-500 flex items-center gap-1 truncate">
                              <LinkIcon className="w-3 h-3" />
                              {video.vimeo_url}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-gray-500 text-sm">
                            <Eye className="w-4 h-4" />
                            <span>{video.watch_count || 0}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => window.open(video.vimeo_url, '_blank')}
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => startEditVideo(video)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteVideo(video.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      
                      {/* Add Video Button */}
                      <Button 
                        variant="outline" 
                        className="w-full mt-2 border-dashed"
                        onClick={() => startAddVideo(category.id)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Bu Kategoriye Video Ekle
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}

export default VideoCoursesManagement;
